import React, { useState, useRef } from "react";
import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import ExpenseForm from "./ExpensesForm";
import ExpenseTable from "./ExpensesTable";

import {
  useGetExpenseTypesQuery,
  useGetExpenseDetailsQuery,
  useDeleteExpenseMutation,
} from "../../../redux/api/expenses";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import BackButton from "../../../components/BackButton/BackButton";

export default function Expenses() {
  const currentAgency = useSelector(selectCurrentagency);
  const [vendor, setVendor] = useState(""); // ✅ define vendor state
  const [description, setDescription] = useState("");

  // API calls
  const { data: types = [], isError: typesError } = useGetExpenseTypesQuery();
  const {
    data: rows = [],
    isLoading: isExpensesLoading,
    refetch,
  } = useGetExpenseDetailsQuery({
    agencyid: currentAgency.agencyid,
  });

  const [deleteExpense] = useDeleteExpenseMutation();

  // State for Add/Edit form
  const [date, setDate] = useState(dayjs());
  const [amount, setAmount] = useState(5000);
  const [type, setType] = useState("");
  const [mode, setMode] = useState("add"); // 👈 controls add/edit
  const [editRow, setEditRow] = useState(null);

  // 👇 form ref
  const formRef = useRef(null);

  // Handle Edit from table
  const handleOpenEdit = (row) => {
    setEditRow(row);
    setDate(dayjs(row.expdate));

    const matchedType = types.find((t) => t.id === row.ExpenseTypeId);
    setType(matchedType ? matchedType.id : "");
    setAmount(row.amount);
    setMode("edit");

    // 👇 scroll to form
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Delete row
  const handleDelete = async (id) => {
    try {
      await deleteExpense(id).unwrap();
      refetch();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  // Reset form after submit
  const resetForm = () => {
    setDate(dayjs());
    setAmount(5000);
    setType("");
    setEditRow(null);
    setMode("add");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="sm">
        <Box sx={{ marginY: 3 }}>
          {/* Top bar: Back + Title */}
          <Stack
            direction={{ xs: "column" }}
            alignItems={{ xs: "flex-start" }}
            // justifyContent="space-between"
            spacing={5}
          >
            <BackButton sx={{ mb: { xs: 3, sm: 3 } }} />
            <Typography variant="h3">Expense</Typography>
          </Stack>
        </Box>

        <Box sx={{ p: 0 }}>
          <Grid container spacing={3} direction="column" alignItems="stretch">
            {/* Form Section */}
            <Grid item xs={12} mb={6} display="flex" ref={formRef}>
              <Box sx={{ width: "100%" }}>
                <ExpenseForm
                  types={types}
                  date={date}
                  setDate={setDate}
                  amount={amount}
                  setAmount={setAmount}
                  type={type}
                  setType={setType}
                  mode={mode}
                  setMode={setMode}
                  editRow={editRow}
                  setVendor={setVendor} // ✅ pass setVendor
                  vendor={vendor}
                  onSuccess={refetch} // refresh table after add/edit
                  onFinish={resetForm} // reset form after save/update
                  description={description}
                  setDescription={setDescription}
                />
              </Box>
            </Grid>

            {/* Table Section */}
            <Grid item xs={12} sx={{ p: 0, width: "100%" }}>
              <ExpenseTable
                rows={rows}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </LocalizationProvider>
  );
}
